import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';

interface UserInfo {
    name?: string;
    email?: string;
    sub?: string;
    preferred_username?: string;
}

interface UserContextType {
    userInfo: UserInfo | null;
    loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const { authState, oktaAuth } = useOktaAuth();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserInfo = async () => {
            if (authState?.isAuthenticated) {
                try {
                    // Get user info from idToken claims instead of making API call
                    const idToken = authState.idToken;
                    if (idToken?.claims) {
                        const user: UserInfo = {
                            name: idToken.claims.name as string,
                            email: idToken.claims.email as string,
                            sub: idToken.claims.sub as string,
                            preferred_username: idToken.claims.preferred_username as string,
                        };
                        setUserInfo(user);
                    }
                } catch (error) {
                    console.error('Error fetching user info:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setUserInfo(null);
                setLoading(false);
            }
        };

        fetchUserInfo();
    }, [authState, oktaAuth]);

    return (
        <UserContext.Provider value={{ userInfo, loading }}>
            {children}
        </UserContext.Provider>
    );
};
